import { useEffect, useRef } from 'react';
import { Sun, CheckSquare, Settings, Bell, Plus } from 'lucide-react';
import { Button, NavCountBadge, ReviewIcon } from '@/components/ui';
import { ProfileAvatar } from '@/components/domain/shared/ProfileAvatar';
import { useEmailQuota, useMonthlyEmailsReviewed } from '@/hooks';
import { TIERS } from '@/constants/billing';
import { useRegisterOverlay } from '@/stores/overlayStore';

export interface MobileDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  activeItem?: string;
  reviewCount?: number;
  /**
   * Items on Today (overdue + happening today + unscheduled). Neutral tone:
   * Today is an informational count that never empties to a clearable queue,
   * so it never earns the red treatment Review gets. `undefined` while the
   * count is loading.
   */
  todayCount?: number;
  unreadNotifications?: number;
  onNavigate?: (path: string) => void;
  onNotificationsClick?: () => void;
  /**
   * Active household id. Drives the "Emails reviewed this month"
   * count rendered inside the household section.
   */
  householdId?: string | null;
  /**
   * Monotonically incrementing key used to force a re-fetch of the
   * household's reviewed-count and quota. AppShell bumps it from
   * the email status feed (`EmailStatusToast.onEmailsChanged`) so the sidebar card stays in
   * sync after approvals / admin overrides / Stripe webhooks without a
   * full page reload.
   */
  refreshKey?: number;
  householdName?: string;
  householdEmoji?: string;
  householdEmojiColor?: string;
  householdImagePath?: string | null;
  userAccessLevel?: string;
  connectionStatus?:
    | 'connected'
    | 'not_connected'
    | 'connection_issue'
    | 'paused';
  onCreateClick?: () => void;
  /**
   * Label of the create button. With Add to Yaruta on it reads "Add to Yaruta"
   * for everyone who can add (YAR-556, Figma 11697:8486); off, today's.
   */
  createLabel?: string;
  /**
   * When false, hide the Review tab from the nav. The review pipeline
   * is owner-only — invited members can't approve emails into someone
   * else's schedule, so the tab has nothing actionable for them.
   */
  showReviewTab?: boolean;
  /**
   * When false, the emails-processed card links nowhere: no "Upgrade plan"
   * CTA and no tap-through on the card itself. /settings/plan is
   * PRIMARY-only (the page redirects caregivers to /settings) — exposing
   * either to caregivers would just bounce them. Defaults to true so
   * existing consumers keep working.
   */
  showUpgradeCta?: boolean;
  /**
   * When false, the emails-confirmed card is not rendered at all (YAR-489):
   * plan usage is for the people who manage the household — Admin and
   * Co-Admin — not Editors or Viewers. Defaults to true so existing
   * consumers keep working.
   */
  showUsageCard?: boolean;
  /**
   * Opens the Send feedback modal (YAR-482). When set, the drawer shows the
   * feedback card above the emails-processed card; the drawer closes itself
   * on tap, and the caller opens the modal over the current page.
   */
  onFeedbackClick?: () => void;
}

const ALL_NAV_ITEMS = [
  { path: '/today', label: 'Today', icon: Sun },
  { path: '/review', label: 'Review', icon: ReviewIcon },
  { path: '/schedule', label: 'Schedule', icon: CheckSquare },
];

const STATUS_STYLES = {
  connected: {
    label: 'Connected',
    dot: 'bg-success',
    text: 'text-success',
    bg: 'bg-success/10 border-success',
  },
  not_connected: {
    label: 'Not connected',
    dot: 'bg-danger',
    text: 'text-danger',
    bg: 'bg-danger/10 border-danger',
  },
  connection_issue: {
    label: 'Connection issue',
    dot: 'bg-warning',
    text: 'text-warning',
    bg: 'bg-warning/10 border-warning',
  },
  paused: {
    label: 'Paused',
    dot: 'bg-muted-foreground',
    text: 'text-muted-foreground',
    bg: 'bg-muted border-border',
  },
};

export function MobileDrawer({
  isOpen,
  onClose,
  activeItem = '/today',
  reviewCount,
  todayCount,
  unreadNotifications,
  onNavigate,
  onNotificationsClick,
  householdId,
  refreshKey,
  householdName,
  householdEmoji,
  householdEmojiColor,
  householdImagePath,
  userAccessLevel,
  connectionStatus,
  onCreateClick,
  createLabel = 'Create new item',
  showReviewTab = true,
  showUpgradeCta = true,
  showUsageCard = true,
  onFeedbackClick,
}: MobileDrawerProps) {
  const drawerRef = useRef<HTMLDivElement>(null);
  const reviewedThisMonth = useMonthlyEmailsReviewed(householdId, refreshKey);
  // Quota for the household's tier (FREE=20, CORE=100, FAMILY=null/unlimited).
  // `undefined` while loading; render a placeholder so the card doesn't pop.
  const emailQuota = useEmailQuota(householdId, refreshKey);
  const navItems = showReviewTab
    ? ALL_NAV_ITEMS
    : ALL_NAV_ITEMS.filter((item) => item.path !== '/review');

  // Close on escape
  // Pauses (and hides) the bottom toast while the drawer covers it.
  useRegisterOverlay(isOpen);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleNavClick = (path: string) => {
    onNavigate?.(path);
    onClose();
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={[
          'fixed inset-0 bg-black/40 z-[200] transition-opacity duration-200',
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none',
        ].join(' ')}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer panel */}
      <aside
        ref={drawerRef}
        className={[
          'fixed top-0 left-0 bottom-0 w-72 bg-background z-[201] flex flex-col',
          'transition-transform duration-200 ease-out',
          isOpen ? 'translate-x-0' : '-translate-x-full',
        ].join(' ')}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-5">
          <span className="text-3xl font-black tracking-tight text-text-brand">
            Yaruta
          </span>
          {onNotificationsClick && (
            <button
              type="button"
              onClick={() => {
                onNotificationsClick();
                onClose();
              }}
              className="relative p-1.5 rounded-md text-muted-foreground hover:bg-muted transition-colors cursor-pointer"
              aria-label="Notifications"
            >
              <Bell size={24} />
              {unreadNotifications != null && unreadNotifications > 0 && (
                <span className="absolute top-1 right-1 w-1.5 h-1.5 bg-danger-hover rounded-full" />
              )}
            </button>
          )}
        </div>

        {/* Create button — only when the caller wires up a handler.
            VIEWER households deliberately omit it so view-only members
            don't see an affordance for an action they can't perform. */}
        {onCreateClick && (
          <div className="px-3 mb-2">
            <button
              type="button"
              onClick={() => {
                onCreateClick();
                onClose();
              }}
              className="w-full border border-border rounded-md py-1.5 px-2 flex items-center justify-center gap-1.5 transition-colors hover:bg-muted cursor-pointer"
            >
              {/* Plus on the left: an icon that says what the action is
                  leads; one that says what happens next trails (YAR-482). */}
              <Plus
                size={16}
                className="text-muted-foreground"
              />
              <span className="text-base font-semibold text-muted-foreground">
                {createLabel}
              </span>
            </button>
          </div>
        )}

        {/* Nav links */}
        <nav className="flex-1 px-3">
          {navItems.map(({ path, label, icon: Icon }) => {
            const isActive = activeItem === path;
            return (
              <button
                type="button"
                key={path}
                onClick={() => handleNavClick(path)}
                className={[
                  'w-full flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-normal mb-1 transition-colors cursor-pointer',
                  isActive
                    ? 'bg-surface-muted text-primary'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                ].join(' ')}
              >
                <Icon size={20} />
                {label}
                {path === '/review' && (
                  <NavCountBadge
                    count={reviewCount}
                    tone="error"
                  />
                )}
                {path === '/today' && (
                  <NavCountBadge
                    count={todayCount}
                    tone="neutral"
                  />
                )}
              </button>
            );
          })}
        </nav>

        {/* Household footer */}
        {/* Vertical order matches Figma 8315:69004:
            1. "Emails confirmed" card (only when we know the household)
            2. Household row: avatar + name/access + settings
            3. Connection status pill
            ──────────────────────────────────────────────────────────────
            The row used to be a button that opened the household switcher
            (chevron-down affordance included); YAR-269 removed the
            switcher — Yaruta supports one household per account — so this
            is now a plain, non-interactive display. */}
        <div className="px-4 py-4 flex flex-col gap-6">
          {onFeedbackClick && (
            <FeedbackCard
              onSendFeedback={() => {
                onClose();
                onFeedbackClick();
              }}
            />
          )}

          {/* Emails confirmed card — Figma nodes 8740:69723 (normal),
              8740:69724 (warning), 8740:69725 (limit reached) and
              8740:69778 (unlimited / Family). Render once we know the
              household; the card itself decides between bar variants
              and the no-bar Family layout based on quota. */}
          {showUsageCard && householdId && emailQuota !== undefined && (
            <EmailsProcessedCard
              used={reviewedThisMonth}
              quota={emailQuota}
              onOpenPlan={
                showUpgradeCta
                  ? () => handleNavClick('/settings/plan')
                  : undefined
              }
            />
          )}

          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <ProfileAvatar
                imagePath={householdImagePath}
                emoji={householdEmoji}
                color={householdEmojiColor}
                size="md"
              />
              <div className="min-w-0 text-left">
                <p className="text-base font-medium text-foreground truncate">
                  {householdName || 'Household'}
                </p>
                <p className="text-sm text-muted-foreground truncate">
                  {userAccessLevel || ''}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                onNavigate?.('/settings');
                onClose();
              }}
              className="p-1.5 rounded-md text-muted-foreground hover:bg-muted transition-colors cursor-pointer shrink-0"
              aria-label="Settings"
            >
              <Settings size={24} />
            </button>
          </div>

          {/* Connection status badge */}
          {connectionStatus && (
            <span
              className={[
                'inline-flex self-start items-center gap-1.5 px-2 py-1 rounded-full border text-sm',
                STATUS_STYLES[connectionStatus].bg,
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <span
                className={[
                  'w-2 h-2 rounded-full',
                  STATUS_STYLES[connectionStatus].dot,
                ].join(' ')}
              />
              <span className={STATUS_STYLES[connectionStatus].text}>
                {STATUS_STYLES[connectionStatus].label}
              </span>
            </span>
          )}
        </div>
      </aside>
    </>
  );
}

/**
 * `.feedback-card (nav)` — Figma 11065:1010 (YAR-482). Same card values as
 * the emails-processed card below it: 1px `border/default` (#d1d1d1), radius
 * 12, 16 padding. No close, no dismissed state: the card stays.
 *
 * The button is 36px tall as drawn; the `after:` layer pads its tap area to
 * 44px (4px above and below) without changing how it looks.
 */
function FeedbackCard({ onSendFeedback }: { onSendFeedback: () => void }) {
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border-strong bg-background p-4">
      <div className="flex flex-col gap-1 text-sm leading-5">
        <p className="font-medium text-text-heading">
          How is Yaruta working for you?
        </p>
        <p className="font-normal text-text-caption">
          Yaruta is new, and your feedback tells us what to fix first.
        </p>
      </div>
      <Button
        variant="secondary"
        size="md"
        onClick={onSendFeedback}
        className="relative w-full after:absolute after:inset-x-0 after:-inset-y-1 after:content-['']"
      >
        Send feedback
      </Button>
    </div>
  );
}

/**
 * Compact emails-processed card pinned above the household row in the
 * mobile sidebar. 12px border-radius, 16px padding, label + count on
 * top, 4px progress track below. Four states per the Figma design-
 * system frame (nodes 8740:69723 / 8740:69724 / 8740:69725 / 8740:69778):
 *
 *  - Normal (<75%): success-green bar.
 *  - Warning (75–99%): warning-orange bar.
 *  - Limit reached (≥100%): error-red bar and alert copy. The Upgrade CTA
 *    renders on FREE only — YAR-192 AC6: "when and only when plan state is
 *    Free AND usage state is at 100%". Core at its cap gets no button.
 *  - Unlimited (Family tier, quota === null): label + raw count only,
 *    no track, no quota denominator.
 *
 * YAR-192 AC5: tapping anywhere on a metered card opens the Plan subpage,
 * the same destination as the CTA (QA: only the button navigated). The tap
 * target is a stretched button under the content rather than a wrapping
 * one, because the Upgrade CTA is itself a button and buttons can't nest.
 *
 * Renders `—` placeholders while the reviewed count is still loading
 * so the card doesn't pop in once the data resolves.
 */
function EmailsProcessedCard({
  used,
  quota,
  onOpenPlan,
}: {
  used: number | null;
  quota: number | null;
  /** Omitted when the viewer can't reach /settings/plan. */
  onOpenPlan?: () => void;
}) {
  // Unlimited (Family) tier: just label + count, no bar.
  if (quota === null) {
    return (
      <div className="flex flex-col gap-2 rounded-xl border border-border bg-background p-4">
        <div className="flex items-center justify-between text-sm font-medium text-foreground">
          <span>Emails confirmed</span>
          <span>{used ?? '—'}</span>
        </div>
      </div>
    );
  }

  const ratio = used == null || quota === 0 ? 0 : used / quota;
  const isLimitReached = ratio >= 1;
  const isWarning = !isLimitReached && ratio >= 0.75;
  const pct = Math.min(100, Math.max(0, ratio * 100));
  // The tier isn't surfaced here, only its cap — and the cap IS the tier
  // (FREE=20, CORE=100), so compare against the FREE constant rather than
  // a literal.
  const isFreeTier = quota === TIERS.FREE.emailQuota;
  const showUpgrade = isLimitReached && isFreeTier && onOpenPlan;
  // Nominal fill is brand green (#264133, `primary`), not success green
  // (#17825a) — YAR-128. The limit fill is Figma `surface/error/default`
  // #a82222 (`danger-hover` is the token carrying it); `danger` #c94040 is
  // what QA filed on YAR-192.
  const barTone = isLimitReached
    ? 'bg-danger-hover'
    : isWarning
      ? 'bg-warning'
      : 'bg-primary';

  // Figma 8505:40517: border/default #d1d1d1 (`border-strong`), label and
  // count text/content/heading #1a1a1a, track #e5e5e5 (`surface-strong`).
  return (
    <div className="relative flex flex-col gap-2 rounded-xl border border-border-strong bg-background p-4">
      {onOpenPlan && (
        <button
          type="button"
          onClick={onOpenPlan}
          aria-label={`Emails confirmed: ${used ?? '—'} of ${quota}. View plan`}
          className="absolute inset-0 rounded-xl cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
        />
      )}
      <div className="flex items-center justify-between text-sm font-medium text-text-heading">
        <span>Emails confirmed</span>
        <span>
          {used ?? '—'} of {quota}
        </span>
      </div>
      <div
        role="progressbar"
        aria-label="Emails confirmed this month"
        aria-valuenow={used ?? 0}
        aria-valuemin={0}
        aria-valuemax={quota}
        className="h-1 w-full overflow-hidden rounded-full bg-surface-strong"
      >
        <div
          className={[
            'h-full transition-[width] duration-300 ease-out',
            barTone,
          ].join(' ')}
          style={{ width: `${pct}%` }}
        />
      </div>
      {isLimitReached && (
        <>
          <p className="text-sm text-text-error">
            {/* Only the Admin can upgrade; a Co-Admin is told who can
                instead of being sent to a button they don't have. */}
            {onOpenPlan
              ? 'Limit reached, upgrade to continue'
              : 'Limit reached — ask the household admin to upgrade'}
          </p>
          {showUpgrade && (
            // `relative` lifts it above the stretched card button so the
            // tap lands here, not on the card.
            <Button
              variant="primary"
              size="sm"
              onClick={onOpenPlan}
              className="relative self-start"
            >
              Upgrade plan
            </Button>
          )}
        </>
      )}
    </div>
  );
}
